import React, { useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import {
  Policy, useListMyPoliciesQuery, useDeletePolicyMutation,
} from '../../store/api/githubApi';
import { PolicyList } from '../../components/policies/PolicyList';
import { PolicyFormDialog } from '../../components/policies/PolicyFormDialog';
import { useNotification } from '../../hooks/useNotification';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';

export const PoliciesPage: React.FC = () => {
  const { data: myPolicies, isLoading, error } = useListMyPoliciesQuery();
  const [deletePolicy] = useDeletePolicyMutation();
  const { notification, hideNotification, showSuccess, showError } = useNotification();

  const [formOpen, setFormOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);

  const accountPolicies = (myPolicies ?? []).filter((p) => p.tier === 'account');

  const handleNew = () => {
    setEditingPolicy(null);
    setFormOpen(true);
  };

  const handleEdit = (policy: Policy) => {
    setEditingPolicy(policy);
    setFormOpen(true);
  };

  const handleDelete = async (policy: Policy) => {
    if (!window.confirm('Delete this policy? Repos will fall back to the next tier down (or the global default).')) return;
    try {
      await deletePolicy(policy.id).unwrap();
      showSuccess('Policy deleted.');
    } catch (err: any) {
      showError(err?.data?.detail ?? 'Failed to delete policy.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Policies</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
            Account-wide rules that gate every connected repo's PR checks, unless a repo defines
            its own override. Repo-specific policies are managed from that repo's Policies tab.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleNew}>
          New policy
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ px: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>Failed to load policies.</Alert>
        ) : (
          <PolicyList
            policies={accountPolicies}
            editableTier="account"
            onEdit={handleEdit}
            onDelete={handleDelete}
            emptyMessage="No account-wide overrides yet — all repos use the global default (block on any high+ severity security issue)."
          />
        )}
      </Paper>

      <PolicyFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        repositoryId={null}
        editingPolicy={editingPolicy}
      />

      <NotificationSnackbar notification={notification} onClose={hideNotification} />
    </Box>
  );
};

export default PoliciesPage;
